import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustToTimeZone,
  getObjectId,
  Trailer,
  TrailerEntity,
  trailerSelector,
  useGetTrailerQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {TrailerForm, TrailerFormDefaultValues, TrailerFormValues} from "../../components/form/TrailerForm/TrailerForm";
import {useTrailerMutation} from "../../hooks/useTrailerMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: TrailerFormDefaultValues
}

export const TrailerFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <TrailerFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromTrailer = (trailer: TrailerEntity | undefined): TrailerFormDefaultValues => {
  return {
    name: trailer?.name,
    barcode: trailer?.barcode,
    company: trailer?.companyId,
    freightType: trailer?.freightTypeId,
    size: {
      size: trailer?.size,
      maxSize: trailer?.maxSize,
      units: trailer?.sizeUnitOfMeasure,
    },
    weight: {
      number: trailer?.weight,
      units: trailer?.weightUnitOfMeasure,
    },
    doNotUse: trailer?.doNotUse,
  };
};

export const TrailerFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectTrailer = useMemo(() => {
    return trailerSelector()
  }, [])

  const {currTrailer, isLoading} = useGetTrailerQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currTrailer: selectTrailer(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: TrailerFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromTrailer(currTrailer);
  }, [currTrailer, formDefaults]);

  const isEdit = useMemo(() => {
    return currTrailer && !copy
  }, [currTrailer, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.trailer.form.title.edit", "Edit Trailer", {ns: "common"});
    } else {
      return t("page.entity.trailer.form.title.new", "New Trailer", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createTrailer,
    updateTrailer,
    isCreating,
    isUpdating,
  } = useTrailerMutation()

  const onTrailerSubmit = useCallback((formValues: TrailerFormValues) => {
    const patch: Partial<Trailer> = {
      externalId: formValues.name,
      barCode: formValues.barcode,
      companyId: getObjectId(formValues.company),
      waterTypeId: getObjectId(formValues.freightType),
      sizeDecimal: formValues.size.size,
      maxSize: formValues.size.maxSize,
      sizeUnitOfMeasure: formValues.size.units,
      weight: formValues.weight?.number,
      weightUnitOfMeasure: formValues.weight?.units,
      doNotUse: formValues.doNotUse,
    }

    if (isEdit) {
      const trailer: Partial<Trailer> = {
        ...currTrailer.asTypeValue(),
        ...patch,
        lastModifiedDttm: adjustToTimeZone().getTime(),
      };
      updateTrailer(trailer, {onSuccess: () => navigate(-1)})
    } else {
      const trailer: Partial<Trailer> = {
        ...patch,
      };
      createTrailer(trailer, {
        onSuccess: (payload) => navigate(`/trailers/profile?id=${payload.id}`, {replace: true})
      })
    }
  }, [currTrailer, isEdit, createTrailer, updateTrailer, navigate]);

  return (
    <ProtectedPage
      permissions={[isEdit ? "trailer_edit" : "trailer_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <TrailerForm
                  options={{
                    regionId: regionId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onTrailerSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

